import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import { 
  CheckCircle, 
  Pause,
  ArrowBack,
  Refresh,
} from '@mui/icons-material';
import agentService from '../../services/agentService';

const HILApprovalDialog = ({ 
  open, 
  onClose, 
  onApprove, 
  stepInfo, 
  draftOutput, 
  isLoading = false,
  workflowSteps = [],
  runId = null,
  onDraftOutputChange = null,
  waitingGroupIndex = null,
  executionMode = 'workflow'
}) => {
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = structured, 1 = raw
  const [editableData, setEditableData] = useState(null); // Editable structured data
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerateInput, setRegenerateInput] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  useEffect(() => {
    if (draftOutput) {
      if (executionMode === 'team') {
        const output = String(draftOutput);
        setEditableData(output);
        setJsonText(output);
        setJsonError(null);
        setActiveTab(1);
        return;
      }
      try {
        // Parse draftOutput to editable structure
        let data = draftOutput;
        if (typeof data === 'string') {
          data = JSON.parse(data);
        }
        setEditableData(data);
        
        // If it's already a string, use it; otherwise stringify
        const output = typeof draftOutput === 'string' ? draftOutput : JSON.stringify(draftOutput, null, 2);
        setJsonText(output);
        setJsonError(null);
      } catch (e) {
        setEditableData(draftOutput);
        setJsonText(String(draftOutput));
        setJsonError(null);
      }
    } else {
      setEditableData(null);
      setJsonText('');
      setJsonError(null);
    }
  }, [draftOutput, open, executionMode]);

  // Update jsonText when editableData changes
  useEffect(() => {
    if (editableData !== null && activeTab === 0) {
      try {
        const updatedJson = JSON.stringify(editableData, null, 2);
        setJsonText(updatedJson);
        setJsonError(null);
      } catch (e) {
        // If stringify fails, keep current jsonText
      }
    }
  }, [editableData, activeTab]);

  const handleApprove = () => {
    try {
      if (executionMode === 'team') {
        onApprove(jsonText);
        return;
      }
      // Use editableData if available (from Structured Data tab), otherwise parse jsonText
      let parsed;
      if (activeTab === 0 && editableData !== null) {
        // Use the edited structured data
        parsed = editableData;
      } else {
        // Parse from raw text
        try {
          parsed = JSON.parse(jsonText);
        } catch (e) {
          // If not valid JSON, treat as raw string
          parsed = jsonText;
        }
      }
      onApprove(parsed);
    } catch (error) {
      setJsonError('Failed to process output. Please check the format.');
    }
  };

  const handleCancel = () => {
    setJsonText('');
    setJsonError(null);
    onClose();
  };

  const handleRegenerate = () => {
    setRegenerateDialogOpen(true);
    setRegenerateInput('');
    setRegenerateError(null);
  };

  const handleRegenerateClose = () => {
    if (isRegenerating) return;
    setRegenerateDialogOpen(false);
    setRegenerateInput('');
    setRegenerateError(null);
  };

  const handleRegenerateSubmit = async () => {
    if (!runId || waitingGroupIndex === null || (executionMode !== 'team' && !regenerateInput.trim())) {
      setRegenerateError(executionMode === 'team' ? 'Invalid regeneration state' : 'Please provide input for regeneration');
      return;
    }

    setIsRegenerating(true);
    setRegenerateError(null);

    try {
      let regeneratedOutput = null;

      const callbacks = {
        onPause: (data) => {
            // Extract the regenerated output from the pause event
            // Check multiple possible fields for the output
            if (executionMode === 'team') {
              regeneratedOutput = data.draft || data.output_raw || data.waiting_output_raw || null;
            } else if (data.draft) {
              regeneratedOutput = data.draft;
            } else if (data.waiting_output_json) {
              regeneratedOutput = data.waiting_output_json;
            } else if (data.waiting_output_raw) {
              try {
                regeneratedOutput = JSON.parse(data.waiting_output_raw);
              } catch (e) {
                regeneratedOutput = data.waiting_output_raw;
              }
            }
            
            // Update the draft output immediately when we get the pause event
            if (regeneratedOutput !== null && onDraftOutputChange) {
              onDraftOutputChange(regeneratedOutput);
            }
            
            // Close dialog and reset state
            setIsRegenerating(false);
            setRegenerateDialogOpen(false);
            setRegenerateInput('');
          },
          onError: (error) => {
            setRegenerateError(error.message || 'Failed to regenerate step');
            setIsRegenerating(false);
          },
          onCompleted: () => {
            // Stream completed - if we already got output from onPause, it's already updated
            // Just ensure we're not in regenerating state
            setIsRegenerating(false);
            // Dialog should already be closed by onPause, but ensure state is clean
            setRegenerateInput('');
          }
        };

      if (executionMode === 'team') {
        await agentService.regenerateTeamHIL(runId, callbacks);
      } else {
        await agentService.regenerateHILWorkflow(
          runId,
          waitingGroupIndex,
          regenerateInput.trim(),
          callbacks
        );
      }
    } catch (error) {
      console.error('Error regenerating step:', error);
      setRegenerateError(error.message || 'Failed to regenerate step');
      setIsRegenerating(false);
    }
  };

  const validateJSON = () => {
    if (!jsonText.trim()) {
      setJsonError('Output cannot be empty');
      return false;
    }
    try {
      JSON.parse(jsonText);
      setJsonError(null);
      return true;
    } catch (e) {
      // Allow non-JSON strings
      setJsonError(null);
      return true;
    }
  };

  // Handle field value change in structured data
  const handleFieldChange = (path, value) => {
    setEditableData(prev => {
      if (prev === null) return prev;
      
      const newData = Array.isArray(prev) ? [...prev] : { ...prev };
      
      // Handle array paths like "0.key" or "1"
      if (Array.isArray(newData)) {
        const pathParts = path.split('.');
        const arrayIndex = parseInt(pathParts[0]);
        
        if (pathParts.length === 1) {
          // Direct array item edit
          newData[arrayIndex] = value;
        } else {
          // Nested object in array
          if (!newData[arrayIndex] || typeof newData[arrayIndex] !== 'object') {
            newData[arrayIndex] = {};
          }
          const nestedKey = pathParts.slice(1).join('.');
          setNestedValue(newData[arrayIndex], nestedKey, value);
        }
      } else {
        // Handle object paths like "key" or "key.nested"
        setNestedValue(newData, path, value);
      }
      
      return newData;
    });
  };

  // Helper to set nested value in object
  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      return current[key];
    }, obj);
    
    // Try to parse as number or boolean if it looks like one
    let parsedValue = value;
    if (value.trim() !== '') {
      if (value === 'true' || value === 'false') {
        parsedValue = value === 'true';
      } else if (!isNaN(value) && value.trim() !== '') {
        parsedValue = Number(value);
      } else {
        parsedValue = value;
      }
    }
    
    target[lastKey] = parsedValue;
  };

  // Get field value from path
  const getFieldValue = (data, path) => {
    if (!data) return '';
    
    const pathParts = path.split('.');
    let value = data;
    
    for (const part of pathParts) {
      if (Array.isArray(value)) {
        value = value[parseInt(part)];
      } else if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return '';
      }
    }
    
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Render structured data view with editable fields
  const renderStructuredData = () => {
    if (!editableData) return null;

    try {
      // Handle array of outputs (for parallel groups)
      if (Array.isArray(editableData)) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editableData.map((item, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                  Output {index + 1}
                </Typography>
                {typeof item === 'string' ? (
                  <TextField
                    fullWidth
                    multiline
                    value={item}
                    onChange={(e) => handleFieldChange(`${index}`, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {Object.entries(item).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ minWidth: '150px', fontWeight: 500, color: 'text.secondary', pt: 1.5 }}>
                          {key}:
                        </Typography>
                        <TextField
                          fullWidth
                          multiline={typeof value === 'object' || String(value).length > 50}
                          rows={typeof value === 'object' ? 4 : 1}
                          value={getFieldValue(editableData[index], key)}
                          onChange={(e) => handleFieldChange(`${index}.${key}`, e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );
      }

      // Handle object output
      if (typeof editableData === 'object' && editableData !== null) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Object.entries(editableData).map(([key, value]) => (
              <Box key={key} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ minWidth: '180px', fontWeight: 500, color: 'text.secondary', pt: 1.5 }}>
                  {key}:
                </Typography>
                <TextField
                  fullWidth
                  multiline={typeof value === 'object' || String(value).length > 50}
                  rows={typeof value === 'object' ? 4 : 1}
                  value={getFieldValue(editableData, key)}
                  onChange={(e) => handleFieldChange(key, e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Box>
            ))}
          </Box>
        );
      }

      // Handle primitive values
      return (
        <TextField
          fullWidth
          multiline
          value={String(editableData)}
          onChange={(e) => setEditableData(e.target.value)}
          variant="outlined"
          size="small"
        />
      );
    } catch (e) {
      return (
        <Typography variant="body2" color="text.secondary">
          Unable to parse structured data. Please use Raw Text tab.
        </Typography>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleCancel();
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        {/* Header Section */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handleCancel} size="small" sx={{ mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {executionMode === 'team' ? 'Team Execution' : 'Workflow Execution'}
                </Typography>
                {runId && (
                  <Typography variant="caption" color="text.secondary">
                    Run ID: {runId}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label="In Progress" 
                color="warning" 
                size="small"
                sx={{ fontWeight: 500 }}
              />
              <Button
                variant="outlined"
                startIcon={<Pause />}
                onClick={handleCancel}
                disabled={isLoading || isRegenerating}
                size="small"
                sx={{ ml: 1 }}
              >
                Put On Hold
              </Button>
              <Button
                variant="outlined"
                startIcon={isRegenerating ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRegenerate}
                disabled={isLoading || isRegenerating || !runId || waitingGroupIndex === null}
                size="small"
                sx={{ ml: 1 }}
              >
                Regenerate
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={handleApprove}
                disabled={isLoading || isRegenerating || !jsonText.trim()}
                size="small"
                sx={{ backgroundColor: 'primary.main', color: 'white' }}
              >
                Approve and Continue
              </Button>
            </Box>
          </Box>

          {/* Workflow Progress Tracker */}
          {workflowSteps.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                position: 'relative',
                mb: 2
              }}>
                {workflowSteps.map((step, index) => {
                  const isActive = step.status === 'active' || step.status === 'waiting_approval';
                  const isCompleted = step.status === 'completed';
                  const isWaiting = step.status === 'waiting_approval';

                  return (
                    <React.Fragment key={index}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isWaiting
                              ? 'warning.main'
                              : isActive 
                                ? 'primary.main' 
                                : isCompleted 
                                  ? 'success.main' 
                                  : 'grey.300',
                            color: isActive || isCompleted || isWaiting ? 'white' : 'grey.600',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            border: isActive || isWaiting ? '2px solid' : 'none',
                            borderColor: isActive ? 'primary.dark' : isWaiting ? 'warning.dark' : 'transparent',
                            mb: 1,
                          }}
                        >
                          {isCompleted ? '✓' : index + 1}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            textAlign: 'center',
                            fontWeight: isActive || isWaiting ? 600 : 400,
                            color: isWaiting 
                              ? 'warning.main' 
                              : isActive 
                                ? 'primary.main' 
                                : isCompleted 
                                  ? 'success.main' 
                                  : 'text.secondary',
                            fontSize: '0.75rem',
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {step.name}
                        </Typography>
                      </Box>
                      {index < workflowSteps.length - 1 && (
                        <Box
                          sx={{
                            flex: 1,
                            height: '2px',
                            backgroundColor: isCompleted ? 'success.main' : 'grey.300',
                            mx: 1,
                            mt: '-20px',
                            borderStyle: 'dashed',
                            borderWidth: '0 0 2px 0',
                            borderColor: isCompleted ? 'success.main' : 'grey.300',
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>

        {/* Main Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Current Step Info */}
            {stepInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {stepInfo.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {executionMode === 'team'
                    ? 'Review this member response before continuing'
                    : 'Review the output from this step before continuing'}
                </Typography>
              </Box>
            )}

            {/* Output Card */}
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: 1,
            }}>
              {executionMode !== 'team' && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                      }
                    }}
                  >
                    <Tab label="Structured Data" />
                    <Tab label="Raw Text" />
                  </Tabs>
                </Box>
              )}

              {/* Tab Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {jsonError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {jsonError}
                  </Alert>
                )}

                {executionMode !== 'team' && activeTab === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    minHeight: '200px'
                  }}>
                    {editableData !== null ? (
                      renderStructuredData()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No output data available
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    value={jsonText}
                    onChange={(e) => {
                      setJsonText(e.target.value);
                      setJsonError(null);
                      // Try to update editableData when raw text changes
                      try {
                        const parsed = JSON.parse(e.target.value);
                        setEditableData(parsed);
                      } catch (e) {
                        // If not valid JSON, keep editableData as is
                      }
                    }}
                    onBlur={validateJSON}
                    placeholder={executionMode === 'team' ? 'Member response text...' : 'Step output JSON or text...'}
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                )}

                {isLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Processing...
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Regenerate Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleRegenerateClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Regenerate {executionMode === 'team' ? 'Member' : 'Step'} {waitingGroupIndex !== null ? waitingGroupIndex + 1 : ''}: {stepInfo?.name || (executionMode === 'team' ? 'Member' : 'Step')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={regenerateInput}
              onChange={(e) => setRegenerateInput(e.target.value)}
              placeholder={executionMode === 'team' ? 'Regenerate will rerun the most recent member.' : 'Provide new input for this step; the next step will still run only after you approve.'}
              variant="outlined"
              disabled={isRegenerating}
              helperText={executionMode === 'team' ? 'Team regenerate reruns the most recent member; no JSON editing is needed.' : 'Provide new input for this step; the next step will still run only after you approve.'}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
            {regenerateError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {regenerateError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleRegenerateClose}
            disabled={isRegenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerateSubmit}
            variant="contained"
            disabled={isRegenerating || (executionMode !== 'team' && !regenerateInput.trim())}
            startIcon={isRegenerating ? <CircularProgress size={16} /> : null}
          >
            {isRegenerating ? 'Regenerating...' : 'Run Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default HILApprovalDialog;
